import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Calendar, CheckCircle, Mail, MapPin, Package, Phone } from "lucide-react";
import api from "../lib/api";
import SEO from "../lib/SEO";

const STATUS_STEPS = [
    { key: "pending",    label: "Pending",   description: "We've received your order and will confirm it shortly." },
    { key: "paid",       label: "Confirmed", description: "Payment received. We'll start baking soon!" },
    { key: "fulfilled",  label: "Fulfilled", description: "Your order has been delivered / picked up." },
];

const CANCELLED = { label: "Cancelled", description: "This order was cancelled. Contact us if you have questions." };

export default function OrderTrack() {
    const { token } = useParams();
    const [searchParams] = useSearchParams();
    const [order, setOrder] = useState(null);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        setNotFound(false);
        setOrder(null);
        api.get(`/orders/track/${token}`)
            .then((r) => setOrder(r.data))
            .catch(() => setNotFound(true));
    }, [token]);

    if (notFound) {
        return (
            <main className="pt-32 pb-20 max-w-3xl mx-auto px-4 text-center" data-testid="track-notfound">
                <SEO title="Order not found — Cake Central" path={`/track/${token}`} />
                <h2 className="font-display text-3xl mb-4">Order not found</h2>
                <p className="text-ink-soft mb-6">This tracking link may be invalid or expired.</p>
                <Link to="/" className="btn-pink">Back home</Link>
            </main>
        );
    }
    if (!order) {
        return <main className="pt-32 pb-20 text-center text-ink-soft" data-testid="track-loading">Loading…</main>;
    }

    const isCancelled = order.status === "cancelled";
    const currentIdx = STATUS_STEPS.findIndex((s) => s.key === order.status);

    return (
        <main data-testid="order-track-page" className="pt-24 md:pt-[10rem] pb-20 bg-[#FFF7FB] min-h-[70vh]">
            <SEO title={`Order ${order.order_number} — Cake Central`} path={`/track/${token}`} />
            <div className="max-w-3xl mx-auto px-4 sm:px-6">
                {/* Header */}
                <div className="mb-8">
                    <p className="text-[10px] tracking-[0.4em] uppercase text-blush mb-2">Order tracking</p>
                    <h1 className="font-display text-4xl md:text-5xl text-ink leading-none mb-2">
                        {order.order_number}
                    </h1>
                    <p className="text-sm text-ink-soft">
                        Placed {new Date(order.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" })}
                    </p>
                </div>

                {searchParams.get("paid") === "1" && order.payment_status === "paid" && (
                    <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 p-4 mb-6 flex items-center gap-3" data-testid="payment-received-banner">
                        <CheckCircle size={20} className="shrink-0" />
                        <p className="text-sm font-medium">Payment received, thank you!</p>
                    </div>
                )}

                {/* Progress */}
                <div className="bg-white border border-[#EAE3DE] p-6 mb-4">
                    {isCancelled ? (
                        <div className="text-center py-4">
                            <p className="text-[10px] tracking-[0.4em] uppercase text-gray-500 mb-2">Status</p>
                            <p className="font-display text-2xl text-gray-600">{CANCELLED.label}</p>
                            <p className="text-sm text-ink-soft mt-2">{CANCELLED.description}</p>
                        </div>
                    ) : (
                        <>
                            <div className="flex items-center justify-between mb-4">
                                {STATUS_STEPS.map((s, i) => {
                                    const done = i <= currentIdx;
                                    const active = i === currentIdx;
                                    return (
                                        <div key={s.key} className="flex-1 flex flex-col items-center relative">
                                            <div
                                                className={`w-10 h-10 rounded-full flex items-center justify-center text-xs font-medium z-10 ${
                                                    done ? "bg-blush text-white" : "bg-blush-pale text-ink-soft"
                                                } ${active ? "ring-4 ring-blush/20" : ""}`}
                                            >
                                                {i + 1}
                                            </div>
                                            <p className={`text-[10px] tracking-[0.2em] uppercase mt-3 ${done ? "text-ink" : "text-ink-soft"}`}>
                                                {s.label}
                                            </p>
                                            {i < STATUS_STEPS.length - 1 && (
                                                <div className={`absolute top-5 left-1/2 w-full h-[2px] ${i < currentIdx ? "bg-blush" : "bg-blush-pale"}`} />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                            <p className="text-sm text-ink-soft text-center mt-4">
                                {(STATUS_STEPS[currentIdx] || STATUS_STEPS[0]).description}
                            </p>
                        </>
                    )}
                </div>

                {/* Items */}
                <div className="grid md:grid-cols-[1fr_320px] gap-4">
                    <div className="bg-white border border-[#EAE3DE] p-6">
                        <h2 className="font-display text-xl mb-4 inline-flex items-center gap-2">
                            <Package size={16} className="text-blush" /> Items
                        </h2>
                        <div className="space-y-3">
                            {order.items.map((it, i) => (
                                <div key={i} className="flex justify-between text-sm border-b border-[#EAE3DE] pb-2 last:border-0">
                                    <span>
                                        {it.name} × {it.qty}
                                        {it.option && <span className="text-ink-soft"> ({it.option})</span>}
                                    </span>
                                    <span className="font-medium">${(it.price * it.qty).toFixed(2)}</span>
                                </div>
                            ))}
                        </div>
                        <div className="mt-4 pt-3 border-t border-[#EAE3DE] space-y-1 text-sm">
                            <div className="flex justify-between"><span className="text-ink-soft">Subtotal</span><span>${order.subtotal.toFixed(2)}</span></div>
                            <div className="flex justify-between"><span className="text-ink-soft">Delivery</span><span>${(order.delivery_fee || 0).toFixed(2)}</span></div>
                            <div className="flex justify-between font-display text-lg pt-2 border-t border-[#EAE3DE] mt-2"><span>Total</span><span>${order.total.toFixed(2)} AUD</span></div>
                        </div>
                    </div>

                    <aside className="bg-white border border-[#EAE3DE] p-6 h-fit">
                        <h2 className="font-display text-xl mb-4">Delivery details</h2>
                        <div className="space-y-2 text-sm">
                            <p className="flex items-start gap-2"><MapPin size={14} className="text-blush mt-0.5 shrink-0" /> {order.delivery_address}</p>
                            {order.delivery_date && (
                                <p className="flex items-center gap-2"><Calendar size={14} className="text-blush shrink-0" /> {order.delivery_date}</p>
                            )}
                            <div className="h-px bg-[#EAE3DE] my-3" />
                            <p className="text-[10px] tracking-[0.3em] uppercase text-ink-soft mb-1">Contact</p>
                            <p className="flex items-center gap-2"><Mail size={14} className="text-blush shrink-0" /> {order.customer_email}</p>
                            <p className="flex items-center gap-2"><Phone size={14} className="text-blush shrink-0" /> {order.customer_phone}</p>
                        </div>
                    </aside>
                </div>

                <div className="text-center mt-8">
                    <p className="text-xs text-ink-soft mb-2">Need help with this order?</p>
                    <Link to="/contact" className="btn-pink btn-pink-outline">Contact us</Link>
                </div>
            </div>
        </main>
    );
}
